import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MergedDataSourceInputParameter } from '@/types'
import { dataSourceApi } from '@/api/services'
import { Button } from '@/components/ui/Button'
import { isDropdownParameterType } from '@/lib/sqlPlaceholders'
import {
  hasOnlyOptionalParameters,
  sortMergedParametersTopologically,
} from '@/lib/dataSourceParameters'
import { formatApiError } from '@/lib/apiError'

/** How an optional runtime parameter was resolved before preview/export. */
export type ParamResolution = 'unset' | 'no-value' | 'has-value'

function shouldLoadDropdownOptions(
  def: MergedDataSourceInputParameter,
  resolution: Record<string, ParamResolution>
): boolean {
  if (def.isRequired) return true
  return (resolution[def.name] ?? 'unset') === 'has-value'
}

type ParamOptionsState =
  | { id: string; name: string }[]
  | 'loading'
  | { kind: 'error'; message: string }

function isOptionsError(
  state: ParamOptionsState | undefined
): state is { kind: 'error'; message: string } {
  return typeof state === 'object' && state !== null && !Array.isArray(state) && state.kind === 'error'
}

function optionsItems(state: ParamOptionsState | undefined): { id: string; name: string }[] {
  return Array.isArray(state) ? state : []
}

function initialResolutionForDef(
  def: MergedDataSourceInputParameter,
  applied: Record<string, string>
): ParamResolution {
  if (def.isRequired) return 'has-value'
  if (def.name in applied) {
    return (applied[def.name] ?? '').trim() === '' ? 'no-value' : 'has-value'
  }
  return 'no-value'
}

function buildInitialResolution(
  definitions: MergedDataSourceInputParameter[],
  applied: Record<string, string>
): Record<string, ParamResolution> {
  const out: Record<string, ParamResolution> = {}
  for (const d of definitions) out[d.name] = initialResolutionForDef(d, applied)
  return out
}

export type ParameterRow = {
  id: string
  key: string
  value: string
  keyReadOnly?: boolean
  isRequired?: boolean
}

let rowId = 0
function nextRowId(): string {
  rowId += 1
  return `p-${rowId}`
}

function recordToRows(params: Record<string, string>): ParameterRow[] {
  const entries = Object.entries(params)
  if (entries.length === 0) return [{ id: nextRowId(), key: '', value: '' }]
  return entries.map(([key, value]) => ({ id: nextRowId(), key, value }))
}

function parameterDisplayLabel(def: MergedDataSourceInputParameter): string {
  const c = (def.caption ?? '').trim()
  return c || def.name
}

function directDependents(defs: MergedDataSourceInputParameter[], dep: string): string[] {
  return defs.filter(d => (d.dependsOn ?? []).includes(dep)).map(d => d.name)
}

function allDownstream(defs: MergedDataSourceInputParameter[], root: string): string[] {
  const out = new Set<string>()
  let frontier = [root]
  while (frontier.length > 0) {
    const next: string[] = []
    for (const n of frontier) {
      for (const name of directDependents(defs, n)) {
        if (!out.has(name)) {
          out.add(name)
          next.push(name)
        }
      }
    }
    frontier = next
  }
  return [...out]
}

function definitionsToRows(
  definitions: MergedDataSourceInputParameter[],
  applied: Record<string, string>
): ParameterRow[] {
  const used = new Set(definitions.map(d => d.name))
  const primary: ParameterRow[] = definitions.map(d => ({
    id: nextRowId(),
    key: d.name,
    value: applied[d.name] ?? '',
    keyReadOnly: true,
    isRequired: d.isRequired,
  }))
  const extras: ParameterRow[] = Object.entries(applied)
    .filter(([k]) => !used.has(k))
    .map(([key, value]) => ({
      id: nextRowId(),
      key,
      value,
      keyReadOnly: false,
      isRequired: false,
    }))
  if (primary.length === 0 && extras.length === 0) {
    return [{ id: nextRowId(), key: '', value: '' }]
  }
  return [...primary, ...extras]
}

function rowsToRecord(rows: ParameterRow[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const r of rows) {
    const k = r.key.trim()
    if (!k) continue
    out[k] = r.value
  }
  return out
}

type Props = {
  open: boolean
  applied: Record<string, string>
  /** When non-empty, rows are driven by data source input parameters (names fixed; required validated). */
  definitions?: MergedDataSourceInputParameter[]
  /** Parameter names injected server-side (hidden from UI); treated as satisfied dependencies. */
  autoResolvedDeps?: Set<string>
  onClose: () => void
  onApply: (parameters: Record<string, string>) => void
}

export function ReportParametersModal({
  open,
  applied,
  definitions,
  autoResolvedDeps,
  onClose,
  onApply,
}: Props) {
  const [rows, setRows] = useState<ParameterRow[]>(() => recordToRows(applied))
  const [error, setError] = useState<string | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})
  const [resolution, setResolution] = useState<Record<string, ParamResolution>>({})
  const [optionsByParam, setOptionsByParam] = useState<Record<string, ParamOptionsState>>({})
  const lastFetchFpRef = useRef<Record<string, string>>({})

  const schemaMode = (definitions?.length ?? 0) > 0
  const optionalOnly = useMemo(
    () => (definitions?.length ? hasOnlyOptionalParameters(definitions) : false),
    [definitions]
  )
  const sortedDefs = useMemo(
    () => (definitions?.length ? sortMergedParametersTopologically(definitions) : []),
    [definitions]
  )
  const resolvedDeps = useMemo(() => autoResolvedDeps ?? new Set<string>(), [autoResolvedDeps])

  useEffect(() => {
    if (!open) return
    setError(null)
    if (schemaMode && definitions) {
      setRows(definitionsToRows(definitions, applied))
      const init: Record<string, string> = {}
      for (const d of definitions) init[d.name] = applied[d.name] ?? ''
      setValues(init)
      setResolution(buildInitialResolution(definitions, applied))
    } else {
      setRows(recordToRows(applied))
      setValues({ ...applied })
      setResolution({})
    }
    setOptionsByParam({})
    lastFetchFpRef.current = {}
  }, [open, applied, schemaMode, definitions])

  const depsSatisfied = useCallback(
    (def: MergedDataSourceInputParameter) =>
      (def.dependsOn ?? []).every(dep => {
        if (resolvedDeps.has(dep)) return true
        const mode = resolution[dep]
        if (mode === 'no-value') return true
        if (mode === 'has-value') return (values[dep] ?? '').trim() !== ''
        return false
      }),
    [values, resolution, resolvedDeps]
  )

  useEffect(() => {
    if (!open || !schemaMode || !definitions) return

    async function loadOne(def: MergedDataSourceInputParameter) {
      if (!def.optionSourceId) return
      setOptionsByParam(prev => ({ ...prev, [def.name]: 'loading' }))
      try {
        const res = await dataSourceApi.getParameterOptions(def.optionSourceId, {
          parameterName: def.name,
          parameters: values,
        })
        setOptionsByParam(prev => ({ ...prev, [def.name]: res.items }))
      } catch (e: unknown) {
        setOptionsByParam(prev => ({
          ...prev,
          [def.name]: { kind: 'error', message: formatApiError(e, 'Failed to load options.') },
        }))
      }
    }

    for (const def of sortedDefs) {
      if (!isDropdownParameterType(def.parameterType)) continue
      if (!shouldLoadDropdownOptions(def, resolution)) {
        setOptionsByParam(prev => {
          if (!(def.name in prev)) return prev
          const next = { ...prev }
          delete next[def.name]
          return next
        })
        delete lastFetchFpRef.current[def.name]
        continue
      }
      if (!depsSatisfied(def)) {
        setOptionsByParam(prev => {
          if (!(def.name in prev)) return prev
          const next = { ...prev }
          delete next[def.name]
          return next
        })
        delete lastFetchFpRef.current[def.name]
        continue
      }
      const fp = (def.dependsOn ?? [])
        .filter(d => !resolvedDeps.has(d))
        .map(d => values[d] ?? '')
        .join('\x01')
      if (lastFetchFpRef.current[def.name] === fp) continue
      lastFetchFpRef.current[def.name] = fp
      void loadOne(def)
    }
  }, [open, schemaMode, definitions, sortedDefs, values, resolution, depsSatisfied, resolvedDeps])

  if (!open) return null

  function setResolutionForDef(name: string, mode: ParamResolution) {
    setResolution(r => {
      const next = { ...r, [name]: mode }
      if (definitions) {
        for (const down of allDownstream(definitions, name)) {
          const downDef = definitions.find(d => d.name === down)
          if (downDef && !downDef.isRequired) next[down] = 'no-value'
        }
      }
      return next
    })
    if (mode === 'no-value') {
      setValueForDef(name, '')
    }
  }

  function setValueForDef(name: string, value: string) {
    setValues(v => {
      const next = { ...v, [name]: value }
      if (definitions) {
        for (const down of allDownstream(definitions, name)) {
          next[down] = ''
        }
      }
      return next
    })
    if (definitions) {
      setOptionsByParam(prev => {
        const next = { ...prev }
        for (const down of allDownstream(definitions, name)) {
          delete next[down]
        }
        return next
      })
    }
    setRows(r =>
      r.map(row => (row.key === name ? { ...row, value } : row))
    )
    if (definitions) {
      const def = definitions.find(d => d.name === name)
      if (def && !def.isRequired && value.trim() !== '') {
        setResolution(r => ({ ...r, [name]: 'has-value' }))
      }
    }
  }

  function addRow() {
    setRows(r => [...r, { id: nextRowId(), key: '', value: '', keyReadOnly: false }])
  }

  function removeRow(id: string) {
    const row = rows.find(x => x.id === id)
    if (row?.keyReadOnly) return
    setRows(r => (r.length <= 1 ? r : r.filter(x => x.id !== id)))
  }

  function updateRow(id: string, field: 'key' | 'value', value: string) {
    setRows(r => r.map(row => (row.id === id ? { ...row, [field]: value } : row)))
  }

  function handleApply() {
    let record: Record<string, string>
    if (schemaMode && definitions) {
      record = { ...rowsToRecord(rows.filter(r => !r.keyReadOnly)) }
      for (const d of definitions) {
        const mode = d.isRequired ? 'has-value' : (resolution[d.name] ?? 'unset')
        if (mode === 'no-value') {
          record[d.name] = ''
        } else if (mode === 'has-value' || !optionalOnly) {
          record[d.name] = values[d.name] ?? ''
        }
      }
    } else {
      record = rowsToRecord(rows)
    }

    if (schemaMode && definitions) {
      const missing = definitions.filter(d => d.isRequired && (record[d.name] ?? '').trim() === '')
      if (missing.length > 0) {
        setError(`Required: ${missing.map(m => parameterDisplayLabel(m)).join(', ')}`)
        return
      }

      if (optionalOnly) {
        const unresolved = definitions.filter(d => (resolution[d.name] ?? 'unset') === 'unset')
        if (unresolved.length > 0) {
          setError(
            `Choose a value or No value for: ${unresolved.map(m => parameterDisplayLabel(m)).join(', ')}`
          )
          return
        }
      }
    }
    setError(null)
    onApply(record)
    onClose()
  }

  function handleCancel() {
    setError(null)
    onClose()
  }

  function renderOptionalResolutionToggle(def: MergedDataSourceInputParameter) {
    const mode = resolution[def.name] ?? 'unset'
    return (
      <div className="mb-2 flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => setResolutionForDef(def.name, 'no-value')}
          className={`rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
            mode === 'no-value'
              ? 'border-accent bg-accent/15 text-accent'
              : 'border-border2 bg-bg text-muted hover:border-accent/40 hover:text-ink'
          }`}
        >
          No value
        </button>
        <button
          type="button"
          onClick={() => setResolutionForDef(def.name, 'has-value')}
          className={`rounded border px-2 py-1 text-[10px] font-medium transition-colors ${
            mode === 'has-value'
              ? 'border-accent bg-accent/15 text-accent'
              : 'border-border2 bg-bg text-muted hover:border-accent/40 hover:text-ink'
          }`}
        >
          Set value
        </button>
      </div>
    )
  }

  function renderSchemaValue(def: MergedDataSourceInputParameter) {
    const v = values[def.name] ?? ''
    const t = def.parameterType ?? 'Dropdown'
    const mode = def.isRequired ? 'has-value' : (resolution[def.name] ?? 'unset')

    if (!def.isRequired) {
      if (mode === 'unset') {
        return (
          <p className="text-[10px] text-muted">
            Choose <span className="font-medium text-ink/80">No value</span> or{' '}
            <span className="font-medium text-ink/80">Set value</span> before continuing.
          </p>
        )
      }
      if (mode === 'no-value') {
        return <p className="text-[10px] text-muted">This parameter will be passed with no value.</p>
      }
    }

    const parentHasNoValue = (def.dependsOn ?? []).some(
      dep => !resolvedDeps.has(dep) && resolution[dep] === 'no-value'
    )

    if (isDropdownParameterType(t)) {
      const ready = depsSatisfied(def)
      const opts = optionsByParam[def.name]
      if (parentHasNoValue) {
        return (
          <p className="text-[10px] text-muted">
            A dependency has no value, so options cannot be loaded. Choose No value or change the parent parameter.
          </p>
        )
      }
      if (!def.optionSourceId) {
        return (
          <p className="text-[10px] text-muted">
            Dropdown has no data source id (merge conflict). Use text or fix data source definitions.
          </p>
        )
      }
      if (!ready) {
        const visibleDeps = (def.dependsOn ?? []).filter(d => !resolvedDeps.has(d))
        return (
          <p className="text-[10px] text-muted">
            Select {visibleDeps.join(', ') || 'dependencies'} first.
          </p>
        )
      }
      if (opts === 'loading') {
        return <p className="text-[10px] text-muted">Loading options…</p>
      }
      if (isOptionsError(opts)) {
        return <p className="text-[10px] text-red-600">{opts.message}</p>
      }
      const items = optionsItems(opts)
      if (t === 'MultiSelect') {
        const selected = v
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
        return (
          <select
            multiple
            className="min-h-[5.5rem] w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
            value={selected}
            onChange={e => {
              const el = e.target as HTMLSelectElement
              const next = [...el.selectedOptions].map(o => o.value)
              setValueForDef(def.name, next.join(','))
            }}
          >
            {(items).map(o => (
              <option key={o.id} value={o.id}>
                {o.name || o.id}
              </option>
            ))}
          </select>
        )
      }
      return (
        <select
          className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
          value={v}
          onChange={e => setValueForDef(def.name, e.target.value)}
        >
          <option value="">—</option>
          {items.map(o => (
            <option key={o.id} value={o.id}>
              {o.name || o.id}
            </option>
          ))}
        </select>
      )
    }

    if (t === 'Calendar') {
      return (
        <input
          type="date"
          className="w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
          value={v}
          onChange={e => setValueForDef(def.name, e.target.value)}
        />
      )
    }

    if (t === 'DateRange') {
      const [from, to] = v.split('|')
      return (
        <div className="flex flex-wrap gap-2">
          <input
            type="date"
            className="min-w-0 flex-1 rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
            value={from ?? ''}
            onChange={e => setValueForDef(def.name, `${e.target.value}|${to ?? ''}`)}
          />
          <input
            type="date"
            className="min-w-0 flex-1 rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
            value={to ?? ''}
            onChange={e => setValueForDef(def.name, `${from ?? ''}|${e.target.value}`)}
          />
        </div>
      )
    }

    return (
      <input
        className="min-w-0 w-full rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
        placeholder="value"
        value={v}
        onChange={e => setValueForDef(def.name, e.target.value)}
      />
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg/80 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-params-title"
    >
      <div className="flex w-full max-w-lg flex-col rounded-lg border border-border bg-surface p-5 shadow-lg">
        <h2 id="report-params-title" className="text-sm font-bold tracking-wide text-ink">
          Report parameters
        </h2>
        <p className="mt-1 text-xs text-muted">
          {schemaMode
            ? optionalOnly
              ? 'All parameters are optional. Choose No value or Set value for each one before preview or export.'
              : 'Values are passed to the template data sources. Required fields must be filled before preview or export.'
            : 'Add name–value pairs passed to preview, PDF, and Excel. Empty names are ignored; duplicate names use the last value.'}
        </p>

        {error ? (
          <p className="mt-3 rounded border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-xs text-red-700 dark:text-red-300">
            {error}
          </p>
        ) : null}

        <div className="mt-4 max-h-[min(50vh,320px)] space-y-2 overflow-y-auto overflow-x-hidden">
          <div className="hidden min-w-0 text-[10px] font-semibold uppercase tracking-wide text-muted sm:grid sm:grid-cols-[minmax(0,9rem)_minmax(0,10.5rem)_auto] sm:gap-2">
            <span>Parameter</span>
            <span>Value</span>
            <span className="text-right"> </span>
          </div>
          {schemaMode && definitions
            ? sortedDefs.map(def => (
                <div
                  key={def.name}
                  className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,9rem)_minmax(0,10.5rem)_auto] sm:items-start"
                >
                  <div className="flex min-w-0 flex-col gap-1 rounded border border-border2 bg-bg px-2 py-1.5">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-ink">
                        {parameterDisplayLabel(def)}
                      </span>
                      {def.isRequired ? (
                        <span className="shrink-0 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                          Required
                        </span>
                      ) : (
                        <span className="shrink-0 rounded bg-border2/60 px-1.5 py-0.5 text-[10px] text-muted">Optional</span>
                      )}
                    </div>
                    <code className="truncate text-[10px] font-mono text-muted">{`{{${def.name}}}`}</code>
                  </div>
                  <div className="min-w-0">
                    {!def.isRequired ? renderOptionalResolutionToggle(def) : null}
                    {renderSchemaValue(def)}
                  </div>
                  <span className="hidden sm:block" />
                </div>
              ))
            : rows.map(row => (
                <div
                  key={row.id}
                  className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,9rem)_minmax(0,10.5rem)_auto] sm:items-start"
                >
                  {row.keyReadOnly ? (
                    <div className="flex min-w-0 items-center gap-2 rounded border border-border2 bg-bg px-2 py-1.5">
                      <code className="min-w-0 flex-1 truncate text-xs font-mono text-ink">{row.key || '—'}</code>
                      {row.isRequired ? (
                        <span className="shrink-0 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent">
                          Required
                        </span>
                      ) : (
                        <span className="shrink-0 rounded bg-border2/60 px-1.5 py-0.5 text-[10px] text-muted">Optional</span>
                      )}
                    </div>
                  ) : (
                    <input
                      className="min-w-0 rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                      placeholder="name"
                      value={row.key}
                      onChange={e => updateRow(row.id, 'key', e.target.value)}
                    />
                  )}
                  <input
                    className="min-w-0 rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                    placeholder="value"
                    value={row.value}
                    onChange={e => updateRow(row.id, 'value', e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="justify-self-start sm:justify-self-end"
                    onClick={() => removeRow(row.id)}
                    disabled={rows.length <= 1 || row.keyReadOnly}
                  >
                    Remove
                  </Button>
                </div>
              ))}
          {schemaMode && definitions
            ? rows.some(r => !r.keyReadOnly)
              ? rows
                  .filter(r => !r.keyReadOnly)
                  .map(row => (
                    <div
                      key={row.id}
                      className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,9rem)_minmax(0,10.5rem)_auto] sm:items-start"
                    >
                      <input
                        className="min-w-0 rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                        placeholder="extra name"
                        value={row.key}
                        onChange={e => updateRow(row.id, 'key', e.target.value)}
                      />
                      <input
                        className="min-w-0 rounded border border-border2 bg-bg px-2 py-1.5 text-xs font-mono text-ink focus:border-accent focus:outline-none"
                        placeholder="value"
                        value={row.value}
                        onChange={e => updateRow(row.id, 'value', e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="justify-self-start sm:justify-self-end"
                        onClick={() => removeRow(row.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))
              : null
            : null}
        </div>

        <Button type="button" variant="subtle" size="sm" className="mt-3 self-start" onClick={addRow}>
          Add parameter
        </Button>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  )
}
